import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { AutoSizer, Table, Column, WindowScroller } from 'react-virtualized';

import BrLoader from '@components/BrLoader';
import CellRenderer from './CellRenderer';
import HeaderRenderer from './HeaderRenderer';
import rowRenderer from './RowRenderer';

/* eslint-disable react/no-array-index-key */
export default class TableRenderer extends PureComponent {
  static propTypes = {
    columnMap: PropTypes.objectOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
      }),
    ).isRequired,
    displayColumns: PropTypes.arrayOf(PropTypes.string).isRequired,
    displayData: PropTypes.arrayOf(PropTypes.object).isRequired,
    minWidth: PropTypes.number.isRequired,
    isRowSelectable: PropTypes.bool.isRequired,
    sortConfig: PropTypes.shape({
      columnKey: PropTypes.string.isRequired,
      orderBy: PropTypes.string.isRequired,
    }).isRequired,
    filterConfig: PropTypes.object.isRequired,
    selectedRows: PropTypes.arrayOf(PropTypes.object).isRequired,
    isLoading: PropTypes.bool.isRequired,
    // this is used to set ref to grid to parent, so parent can perform force update on the grid upon data change
    onTableLoaded: PropTypes.func.isRequired,
    onSortClicked: PropTypes.func.isRequired,
    onSetFilter: PropTypes.func.isRequired,
    dataKey: PropTypes.string,
    highlightedRowKeys: PropTypes.array,
    onRowClick: PropTypes.func,
    isDynamicRowHeight: PropTypes.bool,
    computedWidth: PropTypes.number,
    computedHeight: PropTypes.number,
    useBulkActions: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    this.state = { displayEdge: '' }; // may use initial props
  }

  _lastRenderedWidth = this.props.minWidth;

  componentDidMount() {
    this.props.onTableLoaded(this.grid);
  }

  getCheckboxRenderCell = (row, useBulkActions) => {
    let isReadOnly = false;
    let isMultilined = false;

    if (useBulkActions) {
      const { isEditable, isLive, audiences } = row.Original_Data;
      isReadOnly = !isEditable || isLive;
      const startedAt = row.StartedAt.value;
      const endedAt = row.EndedAt.value;
      if (audiences?.length > 0 || (startedAt !== null && endedAt !== null)) {
        isMultilined = true;
      }
    }
    return {
      value: this.props.selectedRows.includes(row),
      disabled: (row && row.selectRowConfig && row.selectRowConfig.disabled) || isReadOnly,
      tooltipText: row && row.selectRowConfig && row.selectRowConfig.tooltipText,
      useBulkActions,
      isMultilined,
    };
  };

  getRowHeight = ({ index }) => {
    if (this.props.isDynamicRowHeight) {
      return this.props._cache.rowHeight({ index: index - 1 });
    }
    return index === 0 ? 40 : this.props.displayData[index - 1].height || 72;
  };

  getColumnWidth = ({ index }) => {
    const { columnMap, displayColumns } = this.props;
    const col = columnMap[displayColumns[index]];
    return col.width || col.column.defaultWidth || 200;
  };

  renderGridCell = ({ columnIndex, rowIndex, key, style }) => {
    const isHeaderRow = rowIndex === 0;
    const className = isHeaderRow ? 'br-table-grid-header-cell' : 'br-table-grid-content-cell';
    const columnKey = this.props.displayColumns[columnIndex];
    return (
      <div className={className} key={key} style={style}>
        {isHeaderRow
          ? this.renderHeaderCell({ columnKey })
          : this.renderContentCell({ rowIndex: rowIndex - 1, columnIndex })}
      </div>
    );
  };

  renderHeaderCell = ({ columnKey }) => {
    const {
      columnMap,
      sortConfig: { columnKey: sortColumnKey, orderBy },
      filterConfig,
      onSortClicked,
      onSetFilter,
    } = this.props;
    const column = columnMap[columnKey] || {};
    const sortDirection = sortColumnKey === columnKey ? orderBy : '';
    return HeaderRenderer(column, sortDirection, filterConfig, onSortClicked, onSetFilter);
  };

  renderContentCell = ({ parent, rowIndex, dataKey, columnIndex }) => {
    const {
      displayData,
      displayColumns,
      columnMap,
      isRowSelectable,
      isDynamicRowHeight,
      _cache,
      updateExpandedRowIndex,
      expandedRowIndex,
      useBulkActions,
    } = this.props;
    const row = displayData[rowIndex] || {};
    const col = columnMap[displayColumns[columnIndex]] || {};
    const cell = row[col.key] || {};
    const cellRenderProps = {
      row,
      col,
      cell,
      columnIndex,
      rowIndex,
      parent,
      dataKey,
      _cache,
      isDynamicRowHeight,
      table: this.grid,
      updateExpandedRowIndex,
      expandedRowIndex,
    };
    // fills in col info for checkbox column
    // eslint-disable-next-line eqeqeq
    if (isRowSelectable && columnIndex == 0) {
      cellRenderProps.cell = this.getCheckboxRenderCell(row, useBulkActions);
    }
    return CellRenderer(cellRenderProps);
  };

  // The default overscanIndicesGetter method removes a few rows
  // that are still in viewport while scrolling so we need to override it.
  // we also override the default overscanRowCount for smoother scrolling.
  overscanIndicesGetter = ({
    cellCount, // Number of rows or columns in the current axis
    overscanCellsCount, // Maximum number of cells to over-render in either direction
    startIndex, // Begin of range of visible cells
    stopIndex, // End of range of visible cells
  }) => {
    return {
      overscanStartIndex: Math.max(0, startIndex - overscanCellsCount),
      overscanStopIndex: Math.min(cellCount - 1, stopIndex + overscanCellsCount),
    };
  };

  rowRendererOverride = (rowRendererProps) => {
    const { dataKey, highlightedRowKeys } = this.props;
    return rowRenderer({ ...rowRendererProps, dataKey, highlightedRowKeys });
  };

  render() {
    const {
      columnMap,
      displayColumns,
      displayData,
      minWidth,
      isLoading,
      scrollElement,
      onRowClick,
      isDynamicRowHeight,
      _cache,
      computedWidth,
      computedHeight,
    } = this.props;

    return (
      <div className="br-table-table-renderer">
        {isLoading && (
          <div className="is-loading-icon">
            <BrLoader />
          </div>
        )}
        {!(displayData.length === 0 && isLoading) && (
          <WindowScroller scrollElement={scrollElement}>
            {({ height, isScrolling, scrollTop }) => (
              <AutoSizer disableHeight>
                {({ width }) => {
                  if (isDynamicRowHeight && this._lastRenderedWidth !== width) {
                    this._lastRenderedWidth = width;
                    _cache.clearAll();
                  }

                  return (
                    <Table
                      deferredMeasurementCache={isDynamicRowHeight ? _cache : undefined}
                      autoHeight
                      ref={(grid) => { this.grid = grid; }}
                      headerHeight={40}
                      height={computedHeight && computedHeight > 0 ? computedHeight : height || 800}
                      rowHeight={({ index }) => this.getRowHeight({ index: index + 1 })}
                      isScrolling={isScrolling}
                      rowGetter={({ index }) => displayData[index]}
                      rowCount={displayData.length}
                      width={Math.max(minWidth, width, computedWidth)}
                      scrollTop={scrollTop}
                      overscanIndicesGetter={this.overscanIndicesGetter}
                      overscanRowCount={30}
                      rowRenderer={this.rowRendererOverride}
                      onRowClick={onRowClick}
                    >
                      {displayColumns.map((columnKey, index) => {
                        const column = columnMap[columnKey];
                        return (
                          <Column
                            key={index}
                            dataKey={columnKey}
                            cellRenderer={this.renderContentCell}
                            headerRenderer={() => this.renderHeaderCell({ columnKey })}
                            flexGrow={column.flexGrow || 0}
                            flexShrink={column.flexShrink || 0}
                            label={column.displayName || column.key}
                            width={column.width || (column.column && column.column.defaultWidth) || 200}
                          />
                        );
                      })}
                    </Table>
                  );
                }}
              </AutoSizer>
            )}
          </WindowScroller>
        )}
      </div>
    );
  }
}
